import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "icon", "label", "count", "status", "isSelected", "isNew", "i18n", "isDisabled", "id", "actions"],
    _excluded2 = ["children", "spacing", "onKeyDown", "alignment", "extra", "action", "onClick"],
    _excluded3 = ["children"],
    _excluded4 = ["children", "isSelected", "selectedPanelRef", "id"],
    _excluded5 = ["children", "onChange", "placement", "index", "defaultIndex", "isManual", "className"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { Children, cloneElement, createContext, forwardRef, useContext, useRef, useState, isValidElement } from 'react';
import cx from 'classnames';
import { prefix } from '../../tools/settings';
import { useId } from '../../hooks';
import { assignRef } from '../../tools';
import IconButton from '../IconButton';
import { StyledTab, StyledTabIcon, StyledTabStatus, StyledTabList, StyledTabListExtra, StyledTabListTabs, StyledTabListTabsAction, StyledTabListTabsContainer, StyledTabPanels, StyledTabPanel, StyledTabs, StyledTabActions } from './Tabs.styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var TabContext = createContext();
var Tab = forwardRef(function (_ref, ref) {
  var children = _ref.children,
      icon = _ref.icon,
      label = _ref.label,
      count = _ref.count,
      status = _ref.status,
      isSelected = _ref.isSelected,
      isNew = _ref.isNew,
      _ref$i18n = _ref.i18n,
      i18n = _ref$i18n === void 0 ? {
    "new": 'New'
  } : _ref$i18n,
      isDisabled = _ref.isDisabled,
      id = _ref.id,
      actions = _ref.actions,
      rest = _objectWithoutProperties(_ref, _excluded);

  var statusProps = {
    variant: 'status',
    status: 'neutral'
  };
  return _jsxs(StyledTab, _objectSpread(_objectSpread({
    ref: ref,
    role: "tab",
    id: "tab:".concat(id),
    tabIndex: isSelected ? 0 : -1,
    disabled: isDisabled,
    "aria-label": label,
    "aria-selected": isSelected,
    "aria-disabled": isDisabled,
    "aria-controls": "panel:".concat(id)
  }, rest), {}, {
    children: [_jsxs("span", {
      children: [icon && _jsx(StyledTabIcon, {
        icon: icon,
        size: 14
      }), label, " ", !isNaN(count) && _jsxs("small", {
        children: ["(", count > 0 ? count : 0, ")"]
      }), children && _jsx("em", {
        children: children
      })]
    }), isNew && _jsx(StyledTabStatus, _objectSpread(_objectSpread({}, statusProps), {}, {
      children: i18n["new"]
    })), actions && _jsx(StyledTabActions, {
      children: actions
    })]
  }));
});
var TabList = forwardRef(function (_ref2, ref) {
  var children = _ref2.children,
      _ref2$spacing = _ref2.spacing,
      spacing = _ref2$spacing === void 0 ? 'normal' : _ref2$spacing,
      onKeyDown = _ref2.onKeyDown,
      _ref2$alignment = _ref2.alignment,
      alignment = _ref2$alignment === void 0 ? 'left' : _ref2$alignment,
      extra = _ref2.extra,
      action = _ref2.action,
      onClick = _ref2.onClick,
      rest = _objectWithoutProperties(_ref2, _excluded2);

  var _useContext = useContext(TabContext),
      id = _useContext.id,
      selectedIndex = _useContext.index,
      manualIndex = _useContext.manualIndex,
      onManualTabChange = _useContext.onManualTabChange,
      isManual = _useContext.isManual,
      onChangeTab = _useContext.onChangeTab,
      onFocusPanel = _useContext.onFocusPanel;

  var allNodes = useRef([]);
  var className = cx(_defineProperty({}, "".concat(prefix, "-tabs__tab-list--right"), alignment === 'right'));
  var focusableIndexes = Children.map(children, function (child, index) {
    if (isValidElement(child)) {
      return child.props.isDisabled === true ? null : index;
    }
  }).filter(function (index) {
    return index != null;
  });
  var enabledSelectedIndex = focusableIndexes.indexOf(selectedIndex);
  var count = focusableIndexes.length;

  var updateIndex = function updateIndex(index) {
    var childIndex = focusableIndexes[index];
    allNodes.current[childIndex].focus();
    onChangeTab && onChangeTab(childIndex);
  };

  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      var nextIndex = (enabledSelectedIndex + 1) % count;
      updateIndex(nextIndex);
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();

      var _nextIndex = (enabledSelectedIndex - 1 + count) % count;

      updateIndex(_nextIndex);
    }

    if (event.key === 'Home') {
      event.preventDefault();
      updateIndex(0);
    }

    if (event.key === 'End') {
      event.preventDefault();
      updateIndex(count - 1);
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      onFocusPanel && onFocusPanel();
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  var clones = Children.map(children, function (child, index) {
    var isSelected = isManual ? index === manualIndex : index === selectedIndex;

    var handleClick = function handleClick(event) {
      allNodes.current[index].focus();
      onManualTabChange(index);
      onChangeTab(index);

      if (child.props.onClick) {
        child.props.onClick(event);
      }
    };

    if (isValidElement(child)) {
      return cloneElement(child, {
        ref: function ref(node) {
          return allNodes.current[index] = node;
        },
        isSelected: isSelected,
        onClick: handleClick,
        id: "".concat(id, "-").concat(index)
      });
    }
  });
  return _jsxs(StyledTabList, _objectSpread(_objectSpread({
    "aria-orientation": "horizontal",
    onKeyDown: handleKeyDown,
    spacing: spacing,
    ref: ref,
    className: className
  }, rest), {}, {
    children: [_jsx(StyledTabListTabs, {
      children: _jsxs(StyledTabListTabsContainer, {
        children: [clones, action && _jsx(StyledTabListTabsAction, {
          children: _jsx(IconButton, {
            size: "small",
            variant: "ghost",
            icon: action.icon,
            isDisabled: action.isDisabled,
            isLoading: action.isLoading,
            onClick: action.onClick,
            title: action.title,
            onBlur: action.onBlur
          })
        })]
      })
    }), extra && _jsx(StyledTabListExtra, {
      children: extra
    })]
  }));
});
var TabPanels = forwardRef(function (_ref3, ref) {
  var children = _ref3.children,
      rest = _objectWithoutProperties(_ref3, _excluded3);

  var _useContext2 = useContext(TabContext),
      selectedIndex = _useContext2.index,
      selectedPanelRef = _useContext2.selectedPanelRef,
      id = _useContext2.id,
      isManual = _useContext2.isManual,
      manualIndex = _useContext2.manualIndex;

  var clones = Children.map(children, function (child, index) {
    if (!isValidElement(child)) return;
    return cloneElement(child, {
      isSelected: isManual ? index === manualIndex : index === selectedIndex,
      selectedPanelRef: selectedPanelRef,
      id: "".concat(id, "-").concat(index)
    });
  });
  return _jsx(StyledTabPanels, {
    tabIndex: "-1",
    ref: ref,
    children: clones
  });
});
var TabPanel = forwardRef(function (_ref4, _ref5) {
  var children = _ref4.children,
      isSelected = _ref4.isSelected,
      selectedPanelRef = _ref4.selectedPanelRef,
      id = _ref4.id,
      rest = _objectWithoutProperties(_ref4, _excluded4);

  return _jsx(StyledTabPanel, _objectSpread(_objectSpread({
    ref: function ref(node) {
      if (isSelected) {
        assignRef(selectedPanelRef, node);
      }

      assignRef(_ref5, node);
    },
    role: "tabpanel",
    tabIndex: "-1",
    "aria-labelledby": "tab:".concat(id),
    hidden: !isSelected,
    id: "panel:".concat(id)
  }, rest), {}, {
    children: children
  }));
});
var Tabs = forwardRef(function (_ref6, ref) {
  var _cx2;

  var children = _ref6.children,
      onChange = _ref6.onChange,
      _ref6$placement = _ref6.placement,
      placement = _ref6$placement === void 0 ? 'top' : _ref6$placement,
      controlledIndex = _ref6.index,
      defaultIndex = _ref6.defaultIndex,
      isManual = _ref6.isManual,
      customClassName = _ref6.className,
      rest = _objectWithoutProperties(_ref6, _excluded5);

  var _useRef = useRef(controlledIndex != null),
      isControlled = _useRef.current;

  var selectedPanelRef = useRef();

  var getInitialIndex = function getInitialIndex() {
    if (!isManual) {
      return defaultIndex || 0;
    } else {
      return controlledIndex || defaultIndex || 0;
    }
  };

  var getActualIdx = function getActualIdx() {
    if (isManual) {
      return selectedIndex;
    } else {
      return isControlled ? controlledIndex : selectedIndex;
    }
  };

  var _useState = useState(getInitialIndex),
      _useState2 = _slicedToArray(_useState, 2),
      selectedIndex = _useState2[0],
      setSelectedIndex = _useState2[1];

  var _useState3 = useState(controlledIndex || defaultIndex || 0),
      _useState4 = _slicedToArray(_useState3, 2),
      manualIndex = _useState4[0],
      setManualIndex = _useState4[1];

  var actualIdx = getActualIdx();
  var manualIdx = isControlled ? controlledIndex : manualIndex;

  var onChangeTab = function onChangeTab(index) {
    if (!isControlled) {
      setSelectedIndex(index);
    }

    if (isControlled && isManual) {
      setSelectedIndex(index);
    }

    if (!isManual) {
      onChange && onChange(index);
    }
  };

  var onManualTabChange = function onManualTabChange(index) {
    if (!isControlled) {
      setManualIndex(index);
    }

    if (isManual) {
      onChange && onChange(index);
    }
  };

  var onFocusPanel = function onFocusPanel() {
    if (selectedPanelRef.current) {
      selectedPanelRef.current.focus();
    }
  };

  var id = useId();
  var className = cx((_cx2 = {}, _defineProperty(_cx2, "".concat(prefix, "-tabs--bottom"), placement === 'bottom'), _defineProperty(_cx2, customClassName, !!customClassName), _cx2));
  var context = {
    id: id,
    index: actualIdx,
    manualIndex: manualIdx,
    onManualTabChange: onManualTabChange,
    isManual: isManual,
    onChangeTab: onChangeTab,
    selectedPanelRef: selectedPanelRef,
    onFocusPanel: onFocusPanel
  };
  return _jsx(TabContext.Provider, {
    value: context,
    children: _jsx(StyledTabs, _objectSpread(_objectSpread({
      id: id,
      className: className,
      ref: ref
    }, rest), {}, {
      children: children
    }))
  });
});
Tabs.Tab = Tab;
Tabs.List = TabList;
Tabs.Panels = TabPanels;
Tabs.Panel = TabPanel;
Tabs.displayName = 'Tabs';
export default Tabs;
export { Tab, TabList, TabPanels, TabPanel };